import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
  ACCENT, ACCENT_GLOW,
  AGENTS, ERAS, STEPS, WHY_CARDS, VAULT_FILES, FAQ, FOUNDER, FOOTER_COLS,
} from '../components/Landing/landingData';
import Logo from '../components/Landing/Logo';
import NewsletterForm from '../components/Landing/NewsletterForm';
import DwellerSprite from '../components/Landing/DwellerSprite';
import InteractiveBunker from '../components/Landing/InteractiveBunker';
import StoryBand from '../components/Landing/StoryBand';
import StepScreen from '../components/Landing/StepScreens';
import { ClientOnly } from '../components/ClientOnly';

const SILK = "'Silkscreen', monospace";
const PIXEL = "'Press Start 2P', monospace";
const VT = "'VT323', monospace";

const RESPONSIVE_CSS = `
  section[id] { scroll-margin-top: 82px; }
  /* The interactive shell is three columns wide, so it cannot sit beside the
     copy: text on top, shell beneath at the full content width. The shell runs
     past the fold on purpose — seeing it cut off is the scroll cue. */
  .ld-hero { padding: 34px 0 52px; }
  .ld-hero-copy { max-width: 900px; margin: 0 auto 40px; text-align: center; }
  .ld-why-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  .ld-steps-grid { display: grid; grid-template-columns: 1fr auto 1fr auto 1fr; gap: 14px; align-items: stretch; }
  .ld-step-arrow { display: flex; align-items: center; font-family: 'VT323', monospace; font-size: 30px; color: #86d46f; text-shadow: 0 0 14px rgba(134,212,111,0.55); }
  .ld-step-loop { display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 18px; padding: 11px 16px; border: 1px dashed #2c4634; font-family: 'Silkscreen', monospace; font-size: 9px; letter-spacing: 1px; color: #7f9186; }
  .ld-agents-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
  .ld-eras-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; }
  .ld-vault-grid { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 34px; align-items: start; }
  .ld-faq-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .ld-founder-grid { display: grid; grid-template-columns: auto 1fr; gap: 30px; align-items: start; }
  .ld-footer-grid { display: grid; grid-template-columns: 1.4fr 0.8fr 0.8fr 1.2fr; gap: 26px; }
  .ld-cta-grid { display: grid; grid-template-columns: 1fr auto; gap: 30px; align-items: center; }
  .ld-nav-links { display: flex; gap: 30px; }

  /* Nav buttons. The secondaries used to sit at #b9c6bd on a #2a3a2f border —
     barely a step above the background on a CRT-dimmed page. They now carry a
     faint accent wash and a lit edge, and every one of the three answers the
     pointer. */
  .ld-nav-btn, .ld-nav-cta {
    font-family: 'Silkscreen', monospace; font-size: 11px; letter-spacing: 1px;
    white-space: nowrap; padding: 10px 17px; line-height: 1;
    transition: color 140ms ease-out, background 140ms ease-out, border-color 140ms ease-out, box-shadow 140ms ease-out;
  }
  /* Brighter than it looks in the source: the CRT scanline overlay sits above
     the nav and multiplies one row in three by rgba(0,0,0,0.16), so anything
     tuned by eye in DevTools lands a step too dark on the page. */
  .ld-nav-btn {
    color: #eaf6ec; border: 1px solid #6a9b78; background: rgba(134,212,111,0.12);
    text-shadow: 0 0 12px rgba(134,212,111,0.45);
    box-shadow: inset 0 0 14px rgba(134,212,111,0.10), 0 0 16px -6px rgba(134,212,111,0.60);
  }
  .ld-nav-btn:hover, .ld-nav-btn:focus-visible {
    color: #06110a; background: #86d46f; border-color: #86d46f;
    text-shadow: none; box-shadow: 0 0 20px -4px rgba(134,212,111,0.85);
  }
  .ld-nav-cta {
    color: #06110a; background: #9be084; border: 1px solid #b6ec9f;
    box-shadow: 0 0 22px -2px rgba(134,212,111,0.75), 0 0 46px -14px rgba(134,212,111,0.9);
  }
  .ld-nav-cta:hover, .ld-nav-cta:focus-visible {
    background: #b2ec9c; border-color: #d3f7c6;
    box-shadow: 0 0 28px 0 rgba(134,212,111,0.95), 0 0 60px -12px rgba(134,212,111,1);
  }

  /* THE LOOP — printed by the scroll. Each block carries its own reveal
     (StoryBand adds .is-in when it crosses the trigger line), so the stagger
     follows the reader's thumb instead of a fixed clock. */
  .ld-story-block { opacity: 0; transform: translateY(20px); transition: opacity 620ms ease-out, transform 620ms ease-out; }
  .ld-story-block.is-in { opacity: 1; transform: none; }
  @media (prefers-reduced-motion: reduce) {
    .ld-story-block { opacity: 1; transform: none; transition: none; }
  }

  /* The roadmap reads as a frieze: one rail, six stations. */
  .ld-frieze { position: relative; }
  .ld-frieze::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, #2c4634 12%, #2c4634 88%, transparent); }

  @media (max-width: 1020px) {
    .ld-why-grid { grid-template-columns: repeat(2, 1fr); }
    .ld-agents-grid { grid-template-columns: repeat(3, 1fr); }
    .ld-eras-grid { grid-template-columns: repeat(3, 1fr); }
    .ld-frieze::before { display: none; }
  }
  @media (max-width: 900px) {
    .ld-steps-grid { grid-template-columns: 1fr; }
    .ld-step-arrow { justify-content: center; transform: rotate(90deg); }
    .ld-vault-grid { grid-template-columns: 1fr; }
    .ld-faq-grid { grid-template-columns: 1fr; }
    .ld-footer-grid { grid-template-columns: 1fr 1fr; }
    .ld-cta-grid { grid-template-columns: 1fr; }
    .ld-nav-links { display: none; }
  }
  @media (max-width: 560px) {
    .ld-why-grid { grid-template-columns: 1fr; }
    .ld-agents-grid { grid-template-columns: repeat(2, 1fr); }
    .ld-eras-grid { grid-template-columns: repeat(2, 1fr); }
    .ld-founder-grid { grid-template-columns: 1fr; }
    .ld-nav { padding: 0 16px !important; }
    .ld-nav-login { display: none; }
    .ld-nav-cta { padding: 9px 12px; }
  }
`;

export default function LandingPage() {
  return (
    <div style={{ height: '100dvh', overflowX: 'hidden', overflowY: 'auto', background: 'radial-gradient(120% 80% at 70% -10%, #10231a 0%, #0a120d 45%, #070b09 100%)', color: '#c4d2c8', fontFamily: VT, position: 'relative' }}>
      <style>{RESPONSIVE_CSS}</style>
      {/* CRT overlay (scoped to the landing) */}
      <div style={{ pointerEvents: 'none', position: 'fixed', inset: 0, zIndex: 90, background: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0.16) 0px, rgba(0,0,0,0.16) 1px, transparent 1px, transparent 3px)', mixBlendMode: 'multiply' }} />
      <div style={{ pointerEvents: 'none', position: 'fixed', inset: 0, zIndex: 91, boxShadow: 'inset 0 0 220px 40px rgba(0,0,0,0.7)' }} />

      {/* NAV */}
      <nav className="ld-nav" style={{ position: 'sticky', top: 0, zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, height: 66, padding: '0 34px', background: 'rgba(8,13,10,0.86)', backdropFilter: 'blur(6px)', borderBottom: '1px solid #1a2a20' }}>
        <a href="#top" aria-label="FOUNDR-XI — back to top"><Logo size={13} /></a>
        <div className="ld-nav-links" style={{ fontFamily: SILK, fontSize: 10, letterSpacing: 1, color: '#8fa196' }}>
          <a href="#why">WHY</a>
          <a href="#agents">AGENTS</a>
          <a href="#method">HOW IT WORKS</a>
          <a href="#roadmap">ROADMAP</a>
          <a href="#vault">WHAT YOU OWN</a>
          <a href="#faq">FAQ</a>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link className="ld-nav-btn ld-nav-login" to="/pricing">PRICING</Link>
          <Link className="ld-nav-btn ld-nav-login" to="/app">LOG IN</Link>
          <Link className="ld-nav-cta" to="/app">JOIN THE BUNKER</Link>
        </div>
      </nav>

      <div id="top" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px' }}>
        {/* HERO */}
        <section className="ld-hero">
          <div className="ld-hero-copy">
            <div style={{ fontFamily: SILK, fontSize: 'clamp(10px, 1vw, 13px)', letterSpacing: 3, color: ACCENT, marginBottom: 24 }}>THE VALIDATION GATE FOR SOLO FOUNDERS</div>
            {/* Press Start 2P advances a full em per glyph, so the longest
                line ("PROVE PEOPLE WANT IT.", 21 chars) needs ~20x the font
                size. 34px fills the 900px column; the 3.4vw track keeps that
                line unbroken down to 390px. */}
            <h1 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 'clamp(15px, 3.4vw, 34px)', lineHeight: 1.42, margin: '0 0 30px', color: '#f2ecdc', letterSpacing: -1 }}>
              DON&rsquo;T BUILD IT YET.
              <br />
              <span style={{ color: ACCENT, textShadow: `0 0 26px ${ACCENT_GLOW}` }}>PROVE PEOPLE WANT IT.</span>
            </h1>
            <p style={{ fontSize: 'clamp(18px, 2.2vw, 26px)', lineHeight: 1.32, color: '#a6b4aa', maxWidth: 780, margin: '0 auto' }}>
              FOUNDR-XI blocks the next step until the proof is in your files: 20 customer interviews, a chosen beachhead, a tested MVP, a paying customer. Ten AI agents do the research, the scripts and the drafts with you. You make the calls.
            </p>
          </div>
          {/* Canvas + requestAnimationFrame: nothing the prerender can render
              and nothing hydration could match. The placeholder only reserves
              a floor — the shell scales with its column, so its real height is
              width-dependent and no single pixel value is right everywhere. */}
          <ClientOnly placeholder={<div aria-hidden style={{ minHeight: 420 }} />}>
            <InteractiveBunker />
          </ClientOnly>
        </section>
      </div>

      {/* THE LOOP — the diagnosis, before the pitch. */}
      <StoryBand />

      {/* WHY — the pitch: problem, audience, product benefit, game benefit */}
      <section id="why" style={{ padding: '58px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px' }}>
          <div style={{ fontFamily: SILK, fontSize: 12, letterSpacing: 3, color: ACCENT, marginBottom: 10 }}>WHY IT WORKS THIS WAY</div>
          <h2 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 27, lineHeight: 1.45, margin: '0 0 16px', color: '#f2ecdc', letterSpacing: -0.5 }}>
            PLAY THE ONLY GAME THAT PAYS YOU BACK:
            <br />
            <span style={{ color: ACCENT, textShadow: `0 0 22px ${ACCENT_GLOW}` }}>YOUR STARTUP.</span>
          </h2>
          <p style={{ fontSize: 19, lineHeight: 1.3, color: '#8f9f94', maxWidth: 640, margin: '0 0 34px' }}>Building alone is where most startups quietly die. Here&rsquo;s the case for doing it differently.</p>
          <div className="ld-why-grid">
            {WHY_CARDS.map((c) => (
              <div key={c.title} style={{ background: '#0b120e', border: '1px solid #1c2b22', padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 30, height: 30, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: ACCENT, border: `1px solid ${ACCENT}`, background: 'rgba(134,212,111,0.08)' }}>{c.icon}</div>
                  <div style={{ fontFamily: SILK, fontSize: 10, letterSpacing: 0.5, color: '#eaddc6' }}>{c.title}</div>
                </div>
                <p style={{ fontSize: 16, lineHeight: 1.32, color: '#8f9f94', margin: 0 }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AGENTS */}
      <section id="agents" style={{ borderTop: '1px solid #16241c', borderBottom: '1px solid #16241c', background: 'linear-gradient(180deg, #080d0a, #0a120d)', padding: '56px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px' }}>
          <div style={{ fontFamily: SILK, fontSize: 12, letterSpacing: 3, color: ACCENT, marginBottom: 10 }}>YOUR CREW</div>
          <h2 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 22, lineHeight: 1.5, margin: '0 0 12px', color: '#f2ecdc', letterSpacing: -0.5 }}>MEET YOUR AGENTS</h2>
          <p style={{ fontSize: 19, lineHeight: 1.3, color: '#8f9f94', maxWidth: 560, margin: '0 0 34px' }}>Each agent is a specialist with one job, one room, and one obsession. New agents wake up as your startup earns its way deeper into the bunker.</p>
          <div className="ld-agents-grid">
            {AGENTS.map((a) => (
              <div key={a.name} style={{ background: '#0b120e', border: '1px solid #1c2b22', padding: '16px 10px 14px', textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                  <DwellerSprite id={a.id} scale={3} />
                </div>
                <div style={{ fontFamily: VT, fontSize: 19, lineHeight: 1, color: '#e8ddc7', marginBottom: 4 }}>{a.name}</div>
                <div style={{ fontFamily: SILK, fontSize: 7, letterSpacing: 0.5, color: a.color, marginBottom: 8 }}>{a.role}</div>
                <div style={{ fontSize: 15, lineHeight: 1.15, color: '#86968b' }}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="method" style={{ padding: '60px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px' }}>
          <div style={{ fontFamily: SILK, fontSize: 12, letterSpacing: 3, color: ACCENT, marginBottom: 10 }}>THREE STEPS, ON REPEAT</div>
          <h2 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 22, lineHeight: 1.5, margin: '0 0 12px', color: '#f2ecdc', letterSpacing: -0.5 }}>HOW IT WORKS</h2>
          <p style={{ fontSize: 19, lineHeight: 1.3, color: '#8f9f94', maxWidth: 560, margin: '0 0 34px' }}>Give an agent a mission. Get back a real deliverable. Clear the gate. Repeat until you have a business.</p>
          <div className="ld-steps-grid">
            {STEPS.map((s, i) => (
              <Fragment key={s.n}>
                {i > 0 && <div className="ld-step-arrow" aria-hidden="true">▸</div>}
                <div style={{ background: '#0b120e', border: '1px solid #1c2b22', padding: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ fontFamily: PIXEL, fontSize: 18, color: s.glow }}>{s.n}</div>
                    <div style={{ fontFamily: SILK, fontSize: 10, letterSpacing: 0.5, color: '#eaddc6' }}>{s.title}</div>
                  </div>
                  <p style={{ fontSize: 17, lineHeight: 1.28, color: '#8f9f94', margin: '0 0 16px', minHeight: 66 }}>{s.desc}</p>
                  <StepScreen step={s.n as '01' | '02' | '03'} />
                </div>
              </Fragment>
            ))}
          </div>
          <div className="ld-step-loop">
            <span style={{ color: ACCENT, fontSize: 13 }}>↻</span>
            REPEAT 33 TIMES · 6 GATES · UNTIL YOU HAVE A BUSINESS
          </div>
        </div>
      </section>

      {/* ROADMAP / ERAS */}
      <section id="roadmap" style={{ borderTop: '1px solid #16241c', background: 'linear-gradient(180deg, #0a120d, #080d0a)', padding: '58px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px' }}>
          <div style={{ fontFamily: SILK, fontSize: 12, letterSpacing: 3, color: ACCENT, marginBottom: 10 }}>SIX ERAS. ONE FINISH LINE.</div>
          <h2 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 22, lineHeight: 1.5, margin: '0 0 12px', color: '#f2ecdc', letterSpacing: -0.5 }}>FROM IDEA TO EMPIRE</h2>
          <p style={{ fontSize: 19, lineHeight: 1.3, color: '#8f9f94', maxWidth: 560, margin: '0 0 34px' }}>Each era unlocks new agents, new rooms, and a harder boss. You can't skip ahead — that's the point.</p>
          <div className="ld-frieze ld-eras-grid">
            {ERAS.map((e) => (
              <div key={e.n} style={{ position: 'relative', background: '#0b120e', border: '1px solid #1c2b22', borderTop: `3px solid ${e.locked ? '#e3a53f' : ACCENT}`, padding: '16px 12px' }}>
                <div style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 22, color: e.locked ? '#e3a53f' : ACCENT, lineHeight: 1, marginBottom: 8 }}>{e.n}</div>
                <div style={{ fontFamily: SILK, fontSize: 11, letterSpacing: 0.5, color: '#eaddc6', marginBottom: 8 }}>{e.title}</div>
                <div style={{ fontSize: 15, lineHeight: 1.2, color: '#86968b', marginBottom: 14, minHeight: 36 }}>{e.sub}</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: SILK, fontSize: 7, letterSpacing: 0.5, color: '#e3a53f', border: '1px solid #6a4e1f', padding: '4px 6px', background: 'rgba(227,165,63,0.06)' }}><span>⚑</span>{e.boss}</div>
                {/* The one station the story band promised: the build floor a
                    founder cannot walk into before the interviews exist. */}
                {e.locked && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, fontFamily: SILK, fontSize: 7, letterSpacing: 0.5, lineHeight: 1.5, color: '#e3a53f' }}>
                    <span aria-hidden="true">🔒</span>{e.locked}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU OWN — the "real deliverables" claim made literal. Every path
          here is a real deliverable from src/data/quests.ts. */}
      <section id="vault" style={{ borderTop: '1px solid #16241c', background: 'linear-gradient(180deg, #080d0a, #0a120d)', padding: '58px 0' }}>
        <div className="ld-vault-grid" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px' }}>
          <div>
            <div style={{ fontFamily: SILK, fontSize: 12, letterSpacing: 3, color: ACCENT, marginBottom: 10 }}>WHAT YOU WALK AWAY WITH</div>
            <h2 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 22, lineHeight: 1.5, margin: '0 0 16px', color: '#f2ecdc', letterSpacing: -0.5 }}>
              NOT ADVICE.
              <br />
              <span style={{ color: ACCENT, textShadow: `0 0 22px ${ACCENT_GLOW}` }}>FILES.</span>
            </h2>
            <p style={{ fontSize: 19, lineHeight: 1.32, color: '#8f9f94', margin: '0 0 20px' }}>
              Every mission ends as a document in your vault — plain markdown and PDFs, written by the agent who did the work. These are the exact paths the gates check on disk: no file, no open door. Cancel tomorrow and the work stays yours.
            </p>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: SILK, fontSize: 9, letterSpacing: 1, color: '#e3a53f', border: '1px solid #6a4e1f', padding: '8px 10px', background: 'rgba(227,165,63,0.06)' }}>
              <span>⚑</span>33 MISSIONS · 60+ DELIVERABLES
            </div>
          </div>
          <div style={{ background: '#080d0a', border: '1px solid #1c2b22' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #1c2b22', padding: '11px 14px' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e05050' }} />
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e3a53f' }} />
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: ACCENT }} />
              <span style={{ fontFamily: SILK, fontSize: 9, letterSpacing: 1, color: '#8fa196', marginLeft: 6 }}>YOUR-VAULT/</span>
            </div>
            <div style={{ padding: '12px 14px 16px' }}>
              {VAULT_FILES.map((f) => (
                <div key={f.path} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 10px', fontFamily: VT, fontSize: 17, lineHeight: 1.62 }}>
                  <span style={{ color: '#4d6455' }}>▸</span>
                  <span style={{ color: '#b6e5a5' }}>{f.path}</span>
                  <span style={{ color: '#6d7f73' }}>— {f.note}</span>
                </div>
              ))}
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #24382c', fontFamily: VT, fontSize: 17, color: '#7f9186' }}>
                …and the rest of the 33 missions.<span style={{ animation: 'blink 1.1s step-end infinite' }}>_</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUILT IN THE BUNKER — founder proof. Claims here must stay literally true. */}
      <section id="founder" style={{ borderTop: '1px solid #16241c', padding: '58px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px' }}>
          <div style={{ fontFamily: SILK, fontSize: 12, letterSpacing: 3, color: ACCENT, marginBottom: 10 }}>BUILT IN THE BUNKER</div>
          <h2 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 22, lineHeight: 1.5, margin: '0 0 28px', color: '#f2ecdc', letterSpacing: -0.5 }}>
            THE FOUNDER IS <span style={{ color: ACCENT, textShadow: `0 0 22px ${ACCENT_GLOW}` }}>USER #1</span>
          </h2>
          <div className="ld-founder-grid">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, background: '#0b120e', border: '1px solid #1c2b22', padding: '22px 26px' }}>
              <DwellerSprite id="overseer" scale={4} />
              <div style={{ fontFamily: VT, fontSize: 20, color: '#e8ddc7', textAlign: 'center', lineHeight: 1.1 }}>{FOUNDER.name}</div>
              <div style={{ fontFamily: SILK, fontSize: 7, letterSpacing: 0.5, color: ACCENT, textAlign: 'center' }}>{FOUNDER.role}</div>
            </div>
            <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 22 }}>
              {FOUNDER.lines.map((line, i) => (
                <p key={i} style={{ fontSize: 20, lineHeight: 1.34, color: i === 0 ? '#c2d2c6' : '#8f9f94', margin: '0 0 16px', maxWidth: 720 }}>{line}</p>
              ))}
              <div style={{ fontFamily: SILK, fontSize: 9, letterSpacing: 1, color: '#8fa196', marginTop: 4 }}>— DOGFOODED SINCE DAY ONE</div>
              <Link to="/proof" className="ld-nav-btn ld-nav-login" style={{ marginTop: 16, display: 'inline-block' }}>SEE THE FOUNDER&rsquo;S GATE, LIVE &#9654;</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — objections answered in each dweller's own voice. */}
      <section id="faq" style={{ borderTop: '1px solid #16241c', background: 'linear-gradient(180deg, #0a120d, #080d0a)', padding: '58px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px' }}>
          <div style={{ fontFamily: SILK, fontSize: 12, letterSpacing: 3, color: ACCENT, marginBottom: 10 }}>ASK THE CREW</div>
          <h2 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 22, lineHeight: 1.5, margin: '0 0 12px', color: '#f2ecdc', letterSpacing: -0.5 }}>THE HARD QUESTIONS</h2>
          <p style={{ fontSize: 19, lineHeight: 1.3, color: '#8f9f94', maxWidth: 560, margin: '0 0 34px' }}>Answered by the agent whose job it is. Same voice you get inside the bunker.</p>
          <div className="ld-faq-grid">
            {FAQ.map((f) => (
              <div key={f.q} style={{ display: 'flex', flexDirection: 'column', background: '#0b120e', border: '1px solid #1c2b22', padding: 20 }}>
                <div style={{ fontFamily: VT, fontSize: 22, lineHeight: 1.2, color: '#e8ddc7', marginBottom: 12 }}>{f.q}</div>
                <p style={{ fontSize: 17, lineHeight: 1.32, color: '#8f9f94', margin: '0 0 14px' }}>{f.a}</p>
                {/* marginTop:auto keeps the signature on the card floor when the
                    grid row stretches a short answer to match a longer neighbour. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'auto', paddingTop: 12, borderTop: '1px dashed #24382c' }}>
                  <DwellerSprite id={f.id} scale={2} />
                  <div style={{ fontFamily: SILK, fontSize: 8, letterSpacing: 1, color: ACCENT }}>— {f.by}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section id="cta" style={{ borderTop: '1px solid #16241c', borderBottom: '1px solid #16241c', background: 'linear-gradient(180deg, #0b1a13, #081109)' }}>
        <div className="ld-cta-grid" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px', minHeight: 190 }}>
          <div style={{ padding: '34px 0' }}>
            <h2 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 26, lineHeight: 1.5, margin: '0 0 8px', color: '#f2ecdc', letterSpacing: -0.5 }}>READY TO TAKE COMMAND?</h2>
            <p style={{ fontSize: 20, color: '#a6b4aa', margin: '0 0 22px' }}>Your crew is standing by. Your first mission takes five minutes.</p>
            <Link to="/app" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: SILK, fontSize: 13, letterSpacing: 1, color: '#06110a', background: ACCENT, padding: '16px 28px', border: `1px solid ${ACCENT}`, boxShadow: `0 0 30px -6px ${ACCENT}` }}>JOIN THE BUNKER <span>▮</span></Link>
          </div>
          <div style={{ padding: '20px 0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
              {AGENTS.map((a) => (<DwellerSprite key={a.id} id={a.id} scale={2} />))}
            </div>
            <div style={{ height: 6, background: 'repeating-linear-gradient(-45deg, #f0c000 0 8px, #131a15 8px 16px)', opacity: 0.7 }} />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="ld-footer-grid" style={{ maxWidth: 1200, margin: '0 auto', padding: '46px 34px 30px' }}>
        <div>
          <div style={{ marginBottom: 12 }}><Logo size={11} /></div>
          {/* The old tagline here is now the hero headline — the footer gets
              the stat line instead, so the page never says it twice. */}
          <p style={{ fontSize: 16, color: '#7f8f84', margin: '0 0 16px', maxWidth: 240 }}>Ten agents. Six gates. Zero shortcuts.</p>
        </div>
        {FOOTER_COLS.map((col) => (
          <div key={col.head}>
            <div style={{ fontFamily: SILK, fontSize: 9, letterSpacing: 1, color: '#8fa196', marginBottom: 14 }}>{col.head}</div>
            {col.links.map((l) =>
              l.href.startsWith('#') ? (
                <a key={l.label} href={l.href} style={{ display: 'block', fontSize: 16, color: '#97a69b', marginBottom: 9 }}>{l.label}</a>
              ) : (
                <Link key={l.label} to={l.href} style={{ display: 'block', fontSize: 16, color: '#97a69b', marginBottom: 9 }}>{l.label}</Link>
              ),
            )}
          </div>
        ))}
        <div style={{ border: '1px solid #24382c', padding: 16, background: '#0a120d' }}>
          <div style={{ fontFamily: SILK, fontSize: 9, letterSpacing: 1, color: ACCENT, marginBottom: 8 }}>JOIN THE COMMUNITY</div>
          <p style={{ fontSize: 15, color: '#8f9f94', margin: '0 0 12px' }}>Updates, devlogs and dispatches from the bunker.</p>
          <NewsletterForm />
        </div>
      </footer>
      <div style={{ textAlign: 'center', fontFamily: SILK, fontSize: 8, letterSpacing: 1, color: '#5a6a5f', padding: '0 0 26px' }}>© 2026 FOUNDR-XI. ALL RIGHTS RESERVED. NO PAY TO WIN.</div>
    </div>
  );
}
